import React, { useState, useEffect, memo, } from 'react'
import { Divider, Grid, Typography } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import CardLayoutComponent from '../common/card-layout.component'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import stylesheet from '../detail-container.stylesheet'
import NotesComponent from '../common/notes.component'
import ManualOverrideComponent from '../common/manual-override.component'
import EmptyCardComponent from '../common/empty-card.component'
import { formatDisplayName, hasDifferentPrefix } from '../../../../utils/display-name-formatter'

interface IProps {
  input: any
  classes: ClassNameMap
}

const ClinicalScoresComponent: React.FC<IProps> = ({ input, classes }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const patientId: string = patientDetail?.id
  const [data, setData] = useState([] as any)
  const [selectedNotes, setSelectedNotes] = useState([] as number[])

  useEffect(() => {
    if (patientDetail?.measurements?.clinical_scores)
      initData();
  }, [patientDetail?.measurements?.clinical_scores]);

  const initData = () => {
    let clinicalData = input?.isActionItems === true
      ? patientDetail?.measurements?.clinical_scores
        ?.filter((i: any) => i.needs_update === true &&
          i.programs?.includes(input?.actionProgram) &&
          !i.overridden_programs?.includes(input?.actionProgram))
      : patientDetail?.measurements?.clinical_scores

    const stage = clinicalData?.map((e: any) => {
      return { ...e, mode: 'view' }
    })
    setData(stage)
  }

  const formatConfirmDate = (date_recorded: number) => {
    return <Typography
      component={'span'}
      className={classes.subHeaderTitle}
      variant="subtitle2"
      color="textSecondary"
    >
      {date_recorded ? <small>{moment.unix(date_recorded).format('MM/DD/YY')}</small> : ''}
    </Typography>
  }

  const onNotesClick = (_id: any) => {
    if (selectedNotes.includes(+_id)) {
      setSelectedNotes([...selectedNotes.filter((f) => f !== +_id)]);
    } else {
      setSelectedNotes([...selectedNotes, +_id]);
    }
  };

  const rightHeaderCal = (value: any) => {
    return (
      <Typography variant="subtitle2" className={classes.cardValue} align="right">
        <strong>{value ? value : "-"}</strong>
      </Typography>
    );
  };

  const setIcons = () => {
    return input?.isActionItems === true
      ? []
      : [
        { iconType: "description", callback: onNotesClick },
        { iconType: 'more', callback: outer }
      ]
  }

  const outer = (id: any, mode: string) => {
    setData(data?.map((el: any) => (el.id === id ? { ...el, mode: mode } : el)))
  }

  const onOverrideSubmit = (id: any) => outer(id, 'view')

  const getUnit = (unit: any) => {
    unit = (!unit || unit === 'NaN') ? '' : unit
    if (unit)
      return <Typography color="textSecondary" variant="body2" className={classes.desc}>
        <small>&nbsp;({unit})</small>
      </Typography>
  }

  // Sort by ORIGINAL display_name (with prefix) to preserve grouping
  const sortedData = data && Object.keys(data)?.length > 0
    ? [...data].sort((a: any, b: any) =>
        (a.display_name || '').toLowerCase().localeCompare((b.display_name || '').toLowerCase())
      )
    : []

  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
      {sortedData
        ?.sort((a: any, b: any) => a.display_name.toLowerCase() > b.display_name.toLowerCase() ? 1 : -1)
        ?.map((d: any, i: number) => {
          // Check if we need a separator line (prefix changed from previous item)
          const prevItem = i > 0 ? sortedData[i - 1] : null
          const showSeparator = prevItem && hasDifferentPrefix(prevItem.display_name, d.display_name)

          return (
          <React.Fragment key={`cli-scores-${i}`}>
            {/* Light separator line when prefix changes */}
            {showSeparator && (
              <Divider sx={{ my: 0.25, borderColor: 'rgba(0, 0, 0, 0.08)', borderBottomWidth: '0.5px', width: '100%' }} />
            )}
            {d.mode === 'view' && input.parent === 'CS' && (
              <>
                <CardLayoutComponent
                  id={d.id}
                  name={d?.name}
                  leftHeader={formatDisplayName(d.display_name)}
                  rightHeader={rightHeaderCal(d?.value)}
                  subHeader={getUnit(d?.unit || d?.default_unit || '')}
                  bottomText={formatConfirmDate(d?.date_recorded)}
                  needs_confirmation={d.needs_confirmation || false}
                  needs_update={d.needs_update || false}
                  programs={d.programs}
                  is_overridden={d.is_overridden || undefined}
                  overridenPrograms={d.overridden_programs}
                  onOverrideClick={() => outer(d.id, 'override')}
                  icons={setIcons()}
                  onRefClick={() => null}
                  is_referred={d?.is_referred || false}
                  entityRef='clinical_scores'
                  patientId={patientId}
                  category={'measurements'}
                />
              </>
            )}
            {selectedNotes.includes(d.id) && (
              <NotesComponent
                entityId={d.id}
                entityRef={"measurement"}
                cancelBtn={true}
                onCancel={onNotesClick}
              />
            )}
            {d.mode === 'override' && (
              <ManualOverrideComponent
                entityRef='measurement'
                input={d}
                leftHeader={formatDisplayName(d?.display_name) || d?.name}
                rightHeader={rightHeaderCal(d?.value)}
                subHeader={d?.unit || d?.default_unit || ''}
                programs={d.programs}
                closeOverride={() => outer(d.id, 'view')}
                onOverrideSubmit={onOverrideSubmit}
              />
            )}
          </React.Fragment>
        )})}
      {sortedData?.length < 1 && input?.isActionItems !== true && <EmptyCardComponent />}
    </Grid>
  )
}

export default withStyles(stylesheet)(memo(ClinicalScoresComponent))
